import CT from '@marketplace-ai/tags';
import { LoadingSpinner, Table } from '~/components';
import { CostBreakdown } from '~/features/market/listings/components/CostBreakdown';
import { CostCalculatorFailureNotice } from '~/features/market/listings/components/CostCalculatorFailureNotice';
import {
  formatCalculatedMoney,
  hasValidCostCalculation,
} from '~/features/market/listings/components/costCalculatorMoney';
import type { JobListingDetail } from '~/types/hlx/jobListingDetail';
import {
  CALL_DAY_SHIFT_ROW_DATA_KEYS,
  CALL_HOLIDAY_SHIFT_ROW_DATA_KEYS,
  CALL_WEEKEND_SHIFT_ROW_DATA_KEYS,
  HOLIDAY_ROW_DATA_KEYS,
  WEEKEND_ROW_DATA_KEYS,
} from './consts';
import { generateTableColumns } from './tableCols';
import {
  calculateCostPerHour,
  calculateCostPerShift,
  calculatePricePerRVU,
  calculateRvusPerShift,
  checkIfShiftDetails,
  generateRowData,
  type JobCostDetail,
} from './utils';

const TAGS = CT.employer.jobListings.costCalculator;

const accessibleValue = (label: string, value: number | null | undefined) =>
  value == null ? { 'aria-label': `${label}: Unavailable` } : {};

// [MCV-1955] The provider-facing marketplace payload is a partial detail, so the calculator takes
// a partial and narrows it to one with a persisted id before calculating.
const hasPersistedId = (
  detail: Partial<JobListingDetail> | null | undefined
): detail is JobCostDetail => Boolean(detail?.id);

export const CostCalculator = ({
  footer,
  detail,
  loading,
  showHeading = true,
  failureAudience = 'employer',
}: {
  footer?: React.ReactNode;
  detail: Partial<JobListingDetail> | null | undefined;
  loading: boolean;
  showHeading?: boolean;
  failureAudience?: 'employer' | 'provider';
}) => {
  if (loading) return <LoadingSpinner />;

  if (!hasPersistedId(detail)) {
    return (
      <>
        {showHeading && <h3 className="sub-heading-sb mb-3 mt-8">Comprehensive Cost Analysis</h3>}
        <p className="body-text text-grey-300">
          {/* [MCV-1955] A provider viewing a posted listing can't complete the employer's steps. */}
          {failureAudience === 'provider'
            ? 'This listing has no compensation details yet.'
            : 'There are no compensation details to calculate from yet. Go back and complete the Compensation and Labor Metrics steps, then return here.'}
        </p>
        {footer}
      </>
    );
  }

  if (!hasValidCostCalculation(detail)) {
    return (
      <>
        {showHeading && <h3 className="sub-heading-sb mb-3 mt-8">Comprehensive Cost Analysis</h3>}
        <CostCalculatorFailureNotice audience={failureAudience} />
        {footer}
      </>
    );
  }

  const hasShiftDetails = checkIfShiftDetails(detail);
  const costPerHourBase = calculateCostPerHour(detail, 0, detail.shiftDiffType);
  const costPerShiftBase = calculateCostPerShift(detail, costPerHourBase);
  const dayShiftCount = Number(detail.numDayShifts ?? 0);
  // The Day row has no differential, so it bypasses the configurable row helper.
  const weekdayRowData = [
    {
      id: detail.id,
      shiftType: 'Day',
      count: dayShiftCount,
      baseRate: costPerHourBase,
      differential: 0,
      totalRate: costPerHourBase,
      hoursPerShift: detail.hoursPerShift,
      costPerShift: costPerShiftBase,
      subTotal: dayShiftCount * costPerShiftBase,
      rvus: dayShiftCount * calculateRvusPerShift(detail),
      hourlyRate: costPerHourBase,
      pricePerRvu: calculatePricePerRVU(detail, 0, detail.shiftDiffType, costPerHourBase),
    },
    generateRowData(detail, {
      shiftType: 'Evening',
      diffKey: 'eveShiftRateDiff',
      countKey: 'numEveShifts',
      tableIdx: 0,
    }),
    generateRowData(detail, {
      shiftType: 'Night',
      diffKey: 'nightShiftRateDiff',
      countKey: 'numNightShifts',
      tableIdx: 0,
    }),
  ];
  const weekendRowData = WEEKEND_ROW_DATA_KEYS.map((rowData) => generateRowData(detail, rowData));
  const holidayRowData = HOLIDAY_ROW_DATA_KEYS.map((rowData) => generateRowData(detail, rowData));
  const callShiftDayRowData = CALL_DAY_SHIFT_ROW_DATA_KEYS.map((rowData) =>
    generateRowData(detail, rowData)
  );
  const callShiftWeekendRowData = CALL_WEEKEND_SHIFT_ROW_DATA_KEYS.map((rowData) =>
    generateRowData(detail, rowData)
  );
  const callShiftHolidayRowData = CALL_HOLIDAY_SHIFT_ROW_DATA_KEYS.map((rowData) =>
    generateRowData(detail, rowData)
  );

  const derivedTotalContractValue = [
    weekdayRowData,
    weekendRowData,
    holidayRowData,
    callShiftDayRowData,
    callShiftWeekendRowData,
    callShiftHolidayRowData,
  ]
    .flat()
    .reduce((sum, row) => sum + row.subTotal, 0);

  const shiftCount = detail.numOfShifts;
  const persistedTotalContractValue = Number(detail.totalContractValue);
  const totalContractValue = hasShiftDetails
    ? derivedTotalContractValue
    : persistedTotalContractValue;
  const baseContractValue = hasShiftDetails
    ? shiftCount == null
      ? null
      : shiftCount * costPerShiftBase
    : persistedTotalContractValue;
  const diffCost = baseContractValue == null ? null : totalContractValue - baseContractValue;
  const diffPercentage =
    baseContractValue == null || baseContractValue === 0 || diffCost == null
      ? null
      : (diffCost / baseContractValue) * 100;
  const averageCostPerShift =
    shiftCount == null || shiftCount === 0 ? null : totalContractValue / shiftCount;

  return (
    <>
      {showHeading && <h3 className="sub-heading-sb mb-3 mt-8">Comprehensive Cost Analysis</h3>}
      <p>
        Based on the base rate of {formatCalculatedMoney(detail.pricePerHour)} plus shift
        differential
      </p>
      {hasShiftDetails && (
        <>
          <h3 className="sub-heading my-3">Shift-by-Shift Breakdown</h3>
          <h3 className="sub-heading mb-5 mt-8">Weekdays</h3>
          <CostBreakdown label="Weekdays cost breakdown">
            <Table
              tags={TAGS}
              rowData={weekdayRowData}
              columns={generateTableColumns(0)}
              loading={false}
            />
          </CostBreakdown>
          <h3 className="sub-heading mb-5 mt-8">Weekends</h3>
          <CostBreakdown label="Weekends cost breakdown">
            <Table
              tags={TAGS}
              rowData={weekendRowData}
              columns={generateTableColumns(1)}
              loading={false}
            />
          </CostBreakdown>
          <h3 className="sub-heading mb-5 mt-8">Holidays</h3>
          <CostBreakdown label="Holidays cost breakdown">
            <Table
              tags={TAGS}
              rowData={holidayRowData}
              columns={generateTableColumns(2)}
              loading={false}
            />
          </CostBreakdown>
          <h3 className="sub-heading mb-5 mt-8">On-Call Weekday Shifts</h3>
          <CostBreakdown label="On-Call Weekday Shifts cost breakdown">
            <Table
              tags={TAGS}
              rowData={callShiftDayRowData}
              columns={generateTableColumns(3)}
              loading={false}
            />
          </CostBreakdown>
          <h3 className="sub-heading mb-5 mt-8">On-Call Weekend Shifts</h3>
          <CostBreakdown label="On-Call Weekend Shifts cost breakdown">
            <Table
              tags={TAGS}
              rowData={callShiftWeekendRowData}
              columns={generateTableColumns(4)}
              loading={false}
            />
          </CostBreakdown>
          <h3 className="sub-heading mb-5 mt-8">On-Call Holiday Shifts</h3>
          <CostBreakdown label="On-Call Holiday Shifts cost breakdown">
            <Table
              tags={TAGS}
              rowData={callShiftHolidayRowData}
              columns={generateTableColumns(5)}
              loading={false}
            />
          </CostBreakdown>
        </>
      )}
      <div className="mt-8 w-full rounded-lg bg-primary-t-900 p-4">
        <h3 className="sub-heading my-3">Total Contract Value Summary</h3>
        <div className="cost-summary-columns columns-1 [overflow-wrap:anywhere] sm:columns-2">
          <p className="body-text-sb mb-3">Base Compensation (without differentials)</p>
          <p
            className="body-text-sb mb-3"
            data-cy={TAGS.totalCostNoDiff}
            {...accessibleValue('Base Compensation without differentials', baseContractValue)}
          >
            {formatCalculatedMoney(baseContractValue)}
          </p>
          {baseContractValue != null && (
            <p className="body-text mb-3">
              ( {shiftCount} shift X {detail.hoursPerShift}hr X ${detail.pricePerHour}/hr )
            </p>
          )}
          <p className="body-text-sb mb-3">Additional Differential Costs</p>
          <p
            className="body-text-sb mb-3"
            data-cy={TAGS.diffCost}
            {...accessibleValue('Additional Differential Costs', diffCost)}
          >
            {formatCalculatedMoney(diffCost)}
          </p>
          <p className="body-text mb-3">
            {diffPercentage == null
              ? 'Unavailable'
              : `${diffPercentage.toFixed(2)}% above base rate`}
          </p>
        </div>
        <div className="my-4 w-full border-t border-grey-700" />
        <p className="body-text-sb mt-3">Total Compensation (with differentials)</p>
        <p className="mt-3">
          <b data-cy={TAGS.totalCostWithDiff}>{formatCalculatedMoney(totalContractValue)}</b>
        </p>
        <p className="body-text mt-3">
          <span {...accessibleValue('Average Cost Per Shift', averageCostPerShift)}>
            Average Cost Per Shift: {formatCalculatedMoney(averageCostPerShift)}
          </span>{' '}
          |{' '}
          <span {...accessibleValue('Average Cost Per Hour', detail.avgHourlyRate)}>
            Average Cost Per Hour: {formatCalculatedMoney(detail.avgHourlyRate)}
          </span>
        </p>
      </div>
      {footer}
    </>
  );
};
